import {Form, Formik} from 'formik';
import {Navigate, useLocation, useNavigate, useOutletContext} from 'react-router-dom';
import invoke from 'lodash.invoke';
import appLogger from '@core/LoggingProvider';
import ProgressBar from '@components/registration/ProgressBar';
import {Button, Message, TextField} from '@ultradent/components';
import {trimFormData} from '@ultradent/utilities/Strings';
import Validator from '@util/Validator';
import RegistrationService from '@services/RegistrationService';
import Icon from '@components/common/Icon';

const formValidator = new Validator( {
    accountNumber: {
        required: true
    },
    invoiceId: {
        required: true
    }
} );

const VerifyAccount = ( props ) => {
    const navigate = useNavigate();
    const context = useOutletContext();
    if ( !context.authorization( useLocation() ).hasAccountDetails() ) {
        return <Navigate to="/"/>
    }

    return (
        <div>
        <ProgressBar step={'2'} completed={[1, 0, 0]}
                     labels={['create login', 'verify account', 'finalize registration']}/>

            {context.state.dentalLicenseExists && <div>
            <p className="font-normal leading-tight text-center regSubHeading md:text-lg mx-auto">Looks like we already
                have an account on file for this license <strong>{context.state.dentalLicense}</strong>. Now let's
                verify it's you!</p>
            <p className="regSubHeading leading-tight text-center text-sm md:text-base mt-4 mx-auto">If you think this
                is a mistake or you have not purchased from us in the past through a sales rep or dental show, please
                call our customer service at <a href={`tel:${upi.conf.phoneTollFree}`}
                                                className="link-2">{upi.conf.phoneTollFree}</a> to resolve.</p>
        </div>}

            <div className="universalForm-container my-4 mx-auto leading-tight">
            <Formik
                validate={formData => {
                    const {accountNumber, invoiceId} = trimFormData( context.state );

                    return formValidator.validate( {accountNumber, invoiceId} );
                }}
                initialValues={{
                    loading: false
                }}
                onSubmit={( values, {setStatus, setSubmitting} ) => {
                    values.loading = true;
                    RegistrationService.verifyByAccountAndInvoice( context.state.accountNumber,
                            context.state.invoiceId )
                        .then( resp => {

                            const {accountNumber, addresses} = resp;

                            const shippingAddresses = addresses.filter( address => address.addressType === 'Shipping' );

                            if ( accountNumber && shippingAddresses ) {
                                values.loading = false;
                                const addr = shippingAddresses.find( a => a.primary === true );
                                const addressID = addr ? parseInt( addr.id ) : 0;
                                const {dentistFullName, emailAddress, password} = context.state;

                                const registerWebLoginInput = {
                                    dentistFullName,
                                    accountNumber: parseInt( accountNumber ),
                                    defaultShippingAddressID: addressID,
                                    emailAddress,
                                    password
                                };

                                invoke( context, 'setUserAddresses', shippingAddresses );

                                if ( shippingAddresses.length > 1 ) {
                                    invoke( context, 'setStep', 3 );
                                    navigate( '/multipleAddresses' );
                                }
                                else {
                                    RegistrationService.createWebLoginForExistingCustomer( {input: registerWebLoginInput} )
                                        .then( resp => {
                                            invoke( context, 'setStep', 3 );
                                            navigate( '/accountVerifiedNPI', {
                                                state: {isSubmitted: true, emailAddress}
                                            } );
                                        } )
                                        .catch( error => {
                                            appLogger.capture( error );
                                            setSubmitting( false );
                                            setStatus( {serverError: 'There was an error submitting this request, please try again.'} );
                                        } );
                                }
                            }
                            else {
                                setSubmitting( false );
                                values.loading = false;
                                setStatus( {serverError: 'There was an error submitting this request, please try again.'} );
                            }
                        } )
                        .catch( error => {
                            appLogger.capture( error );
                            values.loading = false;
                            setSubmitting( false );
                            if ( error.graphQLErrors ) {
                                error.graphQLErrors.map( error => {
                                    let code = error.extensions.code;
                                    switch ( code ) {
                                        case 'RegistrationIPAddressLockedOut':
                                            invoke( context, 'setRegistrationStatus', true );
                                            navigate( '/registrationLocked', {
                                                state: {errorMessage: error.message}
                                            } );
                                            break;
                                        default:
                                            setStatus( {serverError: error.message} );
                                            break;
                                    }
                                } );
                            }
                            else {
                                setStatus( {serverError: 'There was an error submitting this request, please try again.'} );
                            }
                        } );
                }}
                render={( {values, errors, status, touched, handleSubmit, isSubmitting} ) => (
                    <Form class="universalForm">
                        {status &&
                            <Message notice
                                     className="flex font-normal items-center message-bar -ml-4 -mr-4 md:-ml-8 md:-mr-8 px-4 md:px-8 py-4 text-white w-auto mb-4">
                                <Icon.ui wrapperClassName="mr-2" className="text-white text-xl"
                                         name="report-problem-triangle"/>
                                <span className="message-bar-msg">{status.serverError || status.email}</span>
                            </Message>
                        }
                        <p className="text-xs">* indicates a required field.</p>
                        <TextField
                            label="Customer ID Number*"
                            type="number"
                            name="accountNumber"
                            value={context.state.accountNumber}
                            onChange={context.handleChange}
                            inputClassName="w-full"
                            error={errors.accountNumber}
                            id="ID"/>
                        <TextField
                            label="Invoice Number*"
                            type="number"
                            name="invoiceId"
                            value={context.state.invoiceId}
                            onChange={context.handleChange}
                            inputClassName="w-full"
                            error={errors.invoiceId}
                            id="invoice"/>

                        <p className="text-base mt-4 italic">
                            Use an order invoice you received from us from a previous purchase (shown below)
                        </p>
                        <img className="block mt-8 mx-auto" src="/images/pages/registration/invoiceNumberHelp.png"
                             alt="Invoice Number Example"/>

                        <div className="button-bar flex-row mt-8 mx-0">
                            <span className="button tertiary"
                                  onClick={() => navigate( -1 )}
                                  style="margin-left: 0 !important;">Back</span>
                            <Button loading={values.loading}
                                    disabled={isSubmitting}
                                    primary
                                    onClick={handleSubmit}
                                    style="margin-right: 0 !important;">Next</Button>
                        </div>
                    </Form>
                )}/>
        </div>
    </div>
    )
}

export default VerifyAccount;
